import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { UserKycApplicationDetailsDto, ValidateKycApplicationResponseDto, SaveKycFieldValueItemDto, ConfirmDocumentUploadRequest, UserKycApplicationDocumentDto, CreateDocumentUploadUrlRequest } from '../../../_core/_model/compliance';
import { ComplianceApiService } from '../../../_core/_services/_compliance/compliance-api.service';

@Component({
  selector: 'app-compliance-wizard',
  standalone: false,
  templateUrl: './compliance-wizard.html',
  styleUrl: './compliance-wizard.css',
})
export class ComplianceWizard  implements OnInit {
  applicationId = '';
  loading = false;
  savingFields = false;
  validating = false;
  submitting = false;
  uploadBusy = false;
  deletingDocumentId: string | null = null;

  activeStep = 1;
  errorMessage = '';

  application: UserKycApplicationDetailsDto | null = null;
  validationResult: ValidateKycApplicationResponseDto | null = null;
  fieldsForm: FormGroup;

  draftDocumentTypeCode = '';
  draftExpiryDate = '';
  selectedFile: File | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: ComplianceApiService,
    private fb: FormBuilder
  ) {
    this.fieldsForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.applicationId =
      this.route.snapshot.paramMap.get('id') ||
      this.route.snapshot.paramMap.get('applicationId') ||
      '';

    if (!this.applicationId) {
      this.errorMessage = 'No application selected.';
      return;
    }

    this.loadApplication();
  }

  loadApplication(): void {
    this.loading = true;
    this.errorMessage = '';

    this.api.getApplicationById(this.applicationId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          const app = res?.data || null;
          this.application = app;

          if (!app) {
            this.errorMessage = 'Application not found.';
            return;
          }

          this.buildForm();
          this.patchSavedValues();
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message ||
            err?.error?.Message ||
            'Unable to load application.';
        }
      });
  }

  buildForm(): void {
    const group: Record<string, FormControl> = {};

    (this.application?.fields || []).forEach(field => {
      const validators: any[] = [];

      if (field.isRequired) validators.push(Validators.required);

      group[field.fieldKey] = new FormControl(field.fieldValue || '', validators);
    });

    this.fieldsForm = this.fb.group(group);
  }

  patchSavedValues(): void {
    const app = this.application;
    if (!app) return;

    app.fields.forEach(field => {
      if (this.fieldsForm.contains(field.fieldKey)) {
        this.fieldsForm.get(field.fieldKey)?.setValue(field.fieldValue || '');
      }
    });
  }

  setStep(step: number): void {
    this.activeStep = step;
  }

  canEdit(): boolean {
    const status = this.application?.status;
    return status === 'Draft' || status === 'PendingSubmission' || status === 'Rejected';
  }

  saveFields(): void {
    if (!this.applicationId) return;

    this.fieldsForm.markAllAsTouched();

    if (this.fieldsForm.invalid) {
      this.errorMessage = 'Please complete the required fields.';
      return;
    }

    const payload: SaveKycFieldValueItemDto[] =
      Object.keys(this.fieldsForm.controls).map(key => ({
        fieldKey: key,
        fieldValue: this.fieldsForm.get(key)?.value ?? null
      }));

    this.savingFields = true;
    this.errorMessage = '';

    this.api.saveFieldValues(this.applicationId, { fields: payload })
      .pipe(finalize(() => (this.savingFields = false)))
      .subscribe({
        next: () => {
          this.loadApplication();
          this.activeStep = 2;
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message ||
            'Unable to save field values.';
        }
      });
  }

  onDocumentTypeCodeChanged(value: string): void {
    this.draftDocumentTypeCode = value;
  }

  onExpiryDateChanged(value: string): void {
    this.draftExpiryDate = value;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] || null;
  }

  uploadDocument(): void {
    if (!this.application) return;

    const allowedDocumentTypes =
      this.application.policy?.documents?.map(x => x.documentTypeCode) || [];

    if (!this.draftDocumentTypeCode.trim()) {
      this.errorMessage = 'Document type is required.';
      return;
    }

    if (allowedDocumentTypes.length > 0 &&
        !allowedDocumentTypes.includes(this.draftDocumentTypeCode.trim())) {
      this.errorMessage = 'Selected document type is not valid for this policy.';
      return;
    }

    const selectedDoc =
      this.application.policy?.documents?.find(x => x.documentTypeCode === this.draftDocumentTypeCode.trim()) || null;

    if (selectedDoc?.requiresExpiryDate && !this.draftExpiryDate) {
      this.errorMessage = 'Expiry date is required for the selected document type.';
      return;
    }

    if (!this.selectedFile) {
      this.errorMessage = 'Please choose a file to upload.';
      return;
    }

    this.uploadBusy = true;
    this.errorMessage = '';

    const file = this.selectedFile;

    const presignPayload: CreateDocumentUploadUrlRequest = {
      documentTypeCode: this.draftDocumentTypeCode.trim(),
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream'
    };

    this.api.createDocumentUploadUrl(this.application.applicationId, presignPayload).subscribe({
      next: (presignRes) => {
        const presign = presignRes.data;

        if (!presign) {
          this.uploadBusy = false;
          this.errorMessage = 'Failed to prepare document upload.';
          return;
        }

        this.api.uploadFileToS3(presign.uploadUrl, file).subscribe({
          next: () => {
            const confirmPayload: ConfirmDocumentUploadRequest = {
              documentTypeCode: this.draftDocumentTypeCode.trim(),
              fileName: file.name,
              mimeType: file.type || 'application/octet-stream',
              storageKey: presign.storageKey,
              storageUrl: presign.storageUrl,
              expiryDate: this.draftExpiryDate || null,
              metadataJson: null
            };

            this.api.confirmDocumentUpload(this.application!.applicationId, confirmPayload)
              .pipe(finalize(() => (this.uploadBusy = false)))
              .subscribe({
                next: () => {
                  this.selectedFile = null;
                  this.draftDocumentTypeCode = '';
                  this.draftExpiryDate = '';
                  this.loadApplication();
                },
                error: (err) => {
                  this.errorMessage =
                    err?.error?.message ||
                    'Failed to confirm uploaded document.';
                }
              });
          },
          error: () => {
            this.uploadBusy = false;
            this.errorMessage = 'Failed to upload file to storage.';
          }
        });
      },
      error: (err) => {
        this.uploadBusy = false;
        this.errorMessage =
          err?.error?.message ||
          'Failed to create upload URL.';
      }
    });
  }

  deleteDocument(doc: UserKycApplicationDocumentDto): void {
    if (!this.application) return;

    this.deletingDocumentId = doc.id;
    this.errorMessage = '';

    this.api.deleteDocument(this.application.applicationId, doc.id)
      .pipe(finalize(() => (this.deletingDocumentId = null)))
      .subscribe({
        next: () => {
          this.loadApplication();
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message ||
            'Failed to delete document.';
        }
      });
  }

  validateApplication(): void {
    if (!this.application) return;

    this.validating = true;
    this.errorMessage = '';

    this.api.validateApplication(this.application.applicationId, {})
      .pipe(finalize(() => (this.validating = false)))
      .subscribe({
        next: (res) => {
          this.validationResult = res.data || null;
          this.activeStep = 3;
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message ||
            'Validation failed.';
        }
      });
  }

  continueToReview(): void {
    this.activeStep = 4;
  }

  submitApplication(): void {
    if (!this.application) return;

    this.submitting = true;
    this.errorMessage = '';

    this.api.submitApplication(this.application.applicationId, {})
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: () => {
          this.router.navigate(['/compliance/success', this.application!.applicationId]);
        },
        error: (err) => {
          this.errorMessage = this.getApiErrorMessage(err, 'Failed to submit application.');
        }
      });
  }

  private getApiErrorMessage(err: any, fallback: string): string {
    const directMessage =
      err?.error?.message ||
      err?.error?.Message ||
      err?.error?.details ||
      err?.error?.Details ||
      err?.message;

    if (!directMessage) return fallback;

    const nestedMessage = this.extractNestedProviderMessage(String(directMessage));
    return nestedMessage || String(directMessage);
  }

  private extractNestedProviderMessage(details: string): string | null {
    const jsonStart = details.indexOf('{');
    if (jsonStart < 0) return null;

    try {
      const parsed = JSON.parse(details.slice(jsonStart));
      return parsed?.message ? String(parsed.message) : null;
    } catch {
      return null;
    }
  }
}
